import { useState, useRef, useEffect, useCallback } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";

// Voice-to-text using the browser's built-in Speech Recognition (no keys/cost).
// Big, senior-friendly button. Hidden gracefully if the browser doesn't support it.
export const VoiceButton = ({
  onResult,
  idleLabel = "Tap to speak",
  listeningLabel = "Listening…",
  testId = "voice-button",
  className = "",
}) => {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);
  const cbRef = useRef(onResult);

  useEffect(() => {
    cbRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      const text = Array.from(e.results)
        .map((r) => r[0].transcript)
        .join(" ")
        .trim();
      if (text) cbRef.current(text);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Please allow microphone access, then try again.");
      } else if (e.error !== "aborted") {
        toast.error("Sorry, we didn't catch that. Please try again.");
      }
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => {
      try {
        rec.abort();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const toggle = useCallback(() => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      rec.stop();
      setListening(false);
      return;
    }
    try {
      rec.start();
      setListening(true);
      toast.info("Listening… please speak now.");
    } catch {
      /* start() throws if already started — ignore */
    }
  }, [listening]);

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testId}
      aria-pressed={listening}
      aria-label={listening ? listeningLabel : idleLabel}
      className={`inline-flex items-center justify-center gap-2.5 h-14 px-6 rounded-full font-bold text-lg transition-colors shrink-0 ${
        listening
          ? "bg-destructive text-white"
          : "bg-accent text-white hover:bg-[hsl(196,82%,27%)]"
      } ${className}`}
    >
      <span
        className={`relative grid place-items-center ${listening ? "animate-pulse" : ""}`}
      >
        <Mic className="h-6 w-6" />
        {listening && (
          <span className="absolute -inset-2 rounded-full bg-card/30 animate-ping" />
        )}
      </span>
      {listening ? listeningLabel : idleLabel}
    </button>
  );
};
